<template>
    <div class="flex flex-col grow">
        <DocumentViewerToolbar
            v-model:show-markings="showMarkings"
            :current-page="currentPage"
            :viewer-total-pages="viewerTotalPages"
            @navigate-to-page="onNavigateToPage($event)"
            @zoom-in="onZoomIn"
            @zoom-out="onZoomOut"
            @rotate="onRotate"
            @search="onSearch"
        ></DocumentViewerToolbar>
        <div class="relative flex flex-row grow h-full">
            <div class="w-full document-viewer-container">
                <div :class="['fluid pl-0', { 'lg:pr-6': isShowingDiscussion }]">
                    <!-- Xandr Ads -->
                    <DocumentShowXandrAds
                        v-if="pdfViewerInitialized"
                        class="mt-4 mb-3"
                        :xandr-properties="getAdProperties('_btf')"
                    ></DocumentShowXandrAds>
                    <PdfViewer
                        ref="viewer"
                        v-model:current-page="currentPage"
                        :selected-marking="selectedMarking"
                        :moveable-marking="selectedMarkingArea?.id === -1 ? selectedMarkingArea?.marking : undefined"
                        :enable-moveable-marking="isLoggedIn"
                        :src="document.file_preview"
                        :markings="showMarkings ? markings : []"
                        :offset-top="viewerOffset"
                        @loaded="onViewerLoaded"
                        @request-new-url="handleNewUrlRequest"
                        @update:selected-marking="handleSelectedMarkingUpdate"
                        @update:moveable-marking="
                            selectedMarkingArea = {
                                id: $event ? -1 : null,
                                marking: $event ?? null,
                            }
                        "
                        @click-moveable-marking-cta="triggerPostCreate"
                        @set-viewer-total-pages="handleSetViewerTotalPages($event)"
                    />
                    <Transition name="fade">
                        <DocumentSignUpOverlay
                            v-if="showSignUpOverlay"
                            class="sticky bottom-0"
                        />
                    </Transition>
                    <!-- Xandr Ads -->
                    <DocumentShowXandrAds
                        v-if="pdfViewerInitialized"
                        class="mt-3"
                        :xandr-properties="getAdProperties('_btf_2')"
                    ></DocumentShowXandrAds>
                </div>
                <div class="container my-5 lg:my-10">
                    <div
                        v-if="!document.user_is_uploader"
                        class="rounded bg-white flex flex-col items-center shadow p-5 md:p-6 mt-5"
                    >
                        <h3 class="text-center mb-3 text-2xl font-medium">
                            {{ t('show.send_love_title') }}
                        </h3>
                        <BtnRateDocument
                            :user-vote="document.user_star_vote"
                            :avg-score="document.avg_star_score"
                            :ratings-count="document.ratings_count"
                            :document-id="document.id"
                            size="sm"
                            variant="outlined"
                            :disabled="document.user_is_uploader"
                            @update:user-vote="$store.commit('document/UPDATE_DOCUMENT_USER_STAR_VOTE', $event)"
                            @update:avg-score="$store.commit('document/UPDATE_DOCUMENT_AVG_STAR_SCORE', $event)"
                            @update:ratings-count="$store.commit('document/UPDATE_DOCUMENT_RATINGS_COUNT', $event)"
                        />
                    </div>
                    <TheFooterStatic
                        v-show="screen.minLG"
                        class="mt-10"
                    />
                </div>
            </div>

            <DocumentShowDiscussion
                ref="discussion"
                v-model:selected-marking-area="selectedMarkingArea"
                :document-id="document.id"
                :document-infected="document.infected"
                @post-created="handlePostCreated"
            ></DocumentShowDiscussion>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent, computed } from 'vue';
import { mapGetters } from 'vuex';
import { useWindowScroll } from '@vueuse/core';
import { captureException } from '@sentry/vue';
import { retrieveMarkings } from '@/api/backend/document';
import { removeFromArray } from '@helpers/array';
import DocumentShowXandrAds from '@/components/DocumentShowXandrAds';
import BtnRateDocument from '@/components/BtnRateDocument';
import TheFooterStatic from '@components/TheFooterStatic';
import PdfViewer from '@/components/PdfViewer';
import DocumentSignUpOverlay from '@/components/DocumentSignUpOverlay';
import { provideViewerOffsetKey, provideNewMarkingKey, provideDeleteMarkingKey } from '@/components/keys';
import { useWindowSize } from '@/composables/windowSize';
import { useTranslation } from 'i18next-vue';
import DocumentViewerToolbar from './DocumentPDFViewerToolbar.vue';

export default {
    name: 'DocumentShowDocument',
    components: {
        DocumentViewerToolbar,
        DocumentShowDiscussion: defineAsyncComponent(() => import('./DocumentShowDiscussion.vue')),
        DocumentShowXandrAds,
        BtnRateDocument,
        TheFooterStatic,
        PdfViewer,
        DocumentSignUpOverlay,
    },
    provide() {
        return {
            [provideViewerOffsetKey]: computed(() => this.viewerOffset),
            [provideNewMarkingKey]: computed(() => this.selectedMarkingArea?.marking),
            [provideDeleteMarkingKey]: this.deleteMarking,
        };
    },
    emits: ['set-viewer-total-pages'],
    setup() {
        const { t } = useTranslation('document');
        return {
            windowScrollY: useWindowScroll().y,
            windowHeight: useWindowSize().height,
            t,
        };
    },
    data() {
        return {
            currentPage: 1,
            markings: [],
            loadedMarkingPages: new Set(),
            showMarkings: true,
            selectedMarkingArea: null,
            viewerTotalPages: document.number_of_pages ?? 0,
        };
    },
    computed: {
        ...mapGetters({
            pdfViewerInitialized: 'document/pdfViewerInitialized',
            document: 'document/currentDocument',
            isShowingDiscussion: 'document/isShowingDiscussion',
            screen: 'ui/screen',
            topBarHeight: 'ui/topBarHeight',
            toolbarHeight: 'document/toolbarHeight',
            isLoggedIn: 'auth/isLoggedIn',
        }),
        selectedMarking() {
            const area = this.selectedMarkingArea;
            if (!area?.marking) {
                return null;
            }
            return {
                question_id: area.id,
                ...area.marking,
            };
        },
        hasHighlightedQuestionMarking() {
            return this.selectedMarkingArea && this.selectedMarkingArea.id > 0;
        },
        viewerOffset() {
            return this.topBarHeight + this.toolbarHeight;
        },
        showSignUpOverlay() {
            return !this.isLoggedIn && this.pdfViewerInitialized && this.windowScrollY > this.windowHeight / 2;
        },
    },
    watch: {
        currentPage(newPage) {
            this.loadPageMarkings(newPage);
        },
        selectedMarkingArea() {
            this.showMarkings = true;
        },
    },
    created() {
        if (this.isLoggedIn) {
            this.loadPageMarkings(1);
        }
    },
    methods: {
        getAdProperties(btf = '') {
            const bannerType = this.screen.minMD ? 'billboard' : 'banner';
            return {
                xandrWrapper: `${bannerType}${btf}Wrapper`,
                xandrSlot: `${bannerType}${btf}`,
                adOnDemand: true,
            };
        },
        onNavigateToPage(page) {
            this.currentPage = page;
        },
        onZoomIn() {
            this.$refs.viewer.call('zoomIn');
        },
        onZoomOut() {
            this.$refs.viewer.call('zoomOut');
        },
        onRotate() {
            this.$refs.viewer.call('rotate');
        },
        onSearch(term) {
            this.$refs.viewer.call('search', term);
        },
        async loadPageMarkings(page) {
            const adjacentPages = [page, page + 1];
            if (page > 1) {
                adjacentPages.unshift(page - 1);
            }
            await Promise.all(
                adjacentPages.map(async (p) => {
                    if (!this.loadedMarkingPages.has(p)) {
                        try {
                            const response = await retrieveMarkings(this.document.id, p);
                            this.addDocumentMarkings(response.data);
                            this.loadedMarkingPages.add(p);
                        } catch (error) {
                            captureException(error);
                        }
                    }
                }),
            );
        },
        addDocumentMarkings(newMarkings) {
            this.markings.push(...newMarkings);
        },
        handleSelectedMarkingUpdate(marking) {
            if (!marking) {
                this.resetSelectedMarkingArea();
                return;
            }

            this.selectedMarkingArea = {
                id: marking.question_id || -1,
                marking,
            };
        },
        resetSelectedMarkingArea() {
            this.selectedMarkingArea = {
                id: null,
                marking: null,
            };
        },
        deleteMarking(postId) {
            const markingToDelete = this.markings.find((marking) => marking.question_id === postId);
            if (markingToDelete) {
                removeFromArray(markingToDelete, this.markings, (marking) => marking.question_id === postId);
            }
        },
        handlePostCreated(marking) {
            this.addDocumentMarkings([marking]);
            this.$nextTick(() => {
                this.resetSelectedMarkingArea();
            });
        },
        async onViewerLoaded() {
            await this.$store.dispatch('document/pdfViewerInitialized');
        },
        async handleNewUrlRequest() {
            await this.$store.dispatch('document/reloadDocument');
        },
        triggerPostCreate() {
            this.$refs.discussion.triggerPostCreate();
        },
        handleSetViewerTotalPages(totalPages) {
            this.viewerTotalPages = totalPages;
            if (!this.document.number_of_pages) {
                this.$emit('set-viewer-total-pages', totalPages);
            }
        },
    },
};
</script>
